import {
  ActionIcon,
  Checkbox,
  Combobox,
  Group,
  Input,
  Pill,
  PillsInput,
  useCombobox,
} from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useRef, useState } from "react";

import classes from "./Dropdown.module.css";
import { DropdownChevron } from "./components";
import { getPropsFromComboboxItem, openedInputStyle, useDropdownPosition } from "./utils";

import { IconX } from "@/components/icons";
import { getSmallerSize } from "@/utils/getSmallerSize";

import type { FC } from "react";
import type { DropdownProps } from "./Dropdown";

export type MultiDropdownProps = Omit<DropdownProps, "defaultValue" | "onChange" | "value"> & {
  defaultValues?: string[];
  onChange?: (values?: string[]) => void;
  values?: string[];
};

/**
 * MultiDropdown component for selecting multiple values from a list of options.
 *
 * Supports multi-select, clearable values, pill-style display of selected items, and custom rendering for options.
 * Integrates with Mantine's Combobox, Input, and PillsInput components for accessibility and consistent UI.
 *
 * @component
 * @example
 * <MultiDropdown
 *   label="Select options"
 *   data={["Option 1", "Option 2", { label: "Option 3", value: "3" }]}
 *   values={selected}
 *   onChange={setSelected}
 *   clearable
 *   placeholder="Select..."
 * />
 *
 * @typedef {Object} MultiDropdownProps
 * @property {Array<string | DropdownItem>} data - List of options to display in the dropdown.
 * @property {boolean} [clearable=false] - Show a clear button to remove all selections.
 * @property {string[]} [defaultValues] - The initial selected values.
 * @property {string} [description] - Description text displayed above the dropdown.
 * @property {string} [emptyMessage] - Message to display when no options are available.
 * @property {string} [error] - Error message to display below the dropdown.
 * @property {string} [iconVariant] - Variant for the dropdown chevron icon.
 * @property {Object} [inputProps] - Additional props for the input element.
 * @property {string} [label] - Label text displayed above the dropdown.
 * @property {(values?: string[]) => void} [onChange] - Callback fired when the selected values change.
 * @property {string} [placeholder] - Placeholder text for the dropdown input.
 * @property {string} [size="md"] - Size of the dropdown (e.g., 'sm', 'md', 'lg').
 * @property {string[]} [values] - Controlled selected values for the dropdown.
 * @property {"default" | "checkbox"} [variant="default"] - Option rendering variant.
 *
 * @returns {JSX.Element} MultiDropdown component.
 */
export const MultiDropdown: FC<MultiDropdownProps> = ({
  clearable = false,
  data = [],
  defaultValues,
  description,
  emptyMessage,
  error,
  iconVariant,
  inputProps,
  label,
  onChange,
  placeholder,
  size = "md",
  values = [],
  variant = "default",
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [currentValues, setCurrentValues] = useState(defaultValues || []);
  const [opened, setOpened] = useState(false);
  const position = useDropdownPosition(ref);
  const { ref: groupRef, width } = useElementSize();
  const combobox = useCombobox({
    onDropdownOpen: () => {
      setOpened(true);
      combobox.focusTarget();
    },
    onDropdownClose: () => {
      setOpened(false);
      combobox.resetSelectedOption();
    },
    opened,
  });
  const selected = values.length > 0 ? values : currentValues;
  const isClearable = clearable && selected.length > 0;
  const currentSize = getSmallerSize(size);
  const inputStyle = position ? openedInputStyle[position] : undefined;

  function handleOptionSubmit(submitValue: string) {
    if (onChange) {
      if (isActive(submitValue)) {
        onChange(values.filter((value) => value !== submitValue));
      } else {
        onChange([...values, submitValue]);
      }
    } else {
      setCurrentValues((currentValues) => {
        if (isActive(submitValue)) {
          return currentValues?.filter((value) => value !== submitValue);
        } else {
          return [...currentValues, submitValue];
        }
      });
    }
  }

  function handleValuesClear() {
    if (onChange) {
      onChange(undefined);
    } else {
      setCurrentValues([]);
    }
  }

  function isActive(option?: string | null) {
    if (option) {
      return values.includes(option) || currentValues?.includes(option);
    }

    return false;
  }

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} offset={0} withinPortal={false}>
      <Combobox.DropdownTarget>
        <Input.Wrapper flex={1}>
          {label && <Input.Label size={size}>{label}</Input.Label>}
          {description && <Input.Description size={size}>{description}</Input.Description>}
          <PillsInput
            {...inputProps}
            onClick={() => combobox.toggleDropdown()}
            pointer
            rightSection={
              <Group ref={groupRef} px="md" gap="sm" wrap="nowrap">
                {isClearable && (
                  <ActionIcon
                    color="red"
                    size={currentSize}
                    variant={iconVariant}
                    onClick={handleValuesClear}
                  >
                    <IconX size={24} />
                  </ActionIcon>
                )}
                <DropdownChevron
                  opened={opened}
                  size={currentSize}
                  variant={iconVariant}
                  onClick={combobox.toggleDropdown}
                />
              </Group>
            }
            rightSectionPointerEvents={isClearable ? undefined : "none"}
            rightSectionWidth="auto"
            styles={{ input: opened ? inputStyle : undefined }}
          >
            <Pill.Group mr={width}>
              {selected.length > 0 ? (
                selected.map((item, index) => {
                  const { label, value } = getPropsFromComboboxItem(item);
                  return (
                    <Pill key={index} withRemoveButton onRemove={() => handleOptionSubmit(value)}>
                      {label}
                    </Pill>
                  );
                })
              ) : (
                <Input.Placeholder>{placeholder}</Input.Placeholder>
              )}
              <Combobox.EventsTarget>
                <PillsInput.Field
                  type="hidden"
                  onBlur={() => combobox.closeDropdown()}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && selected.length > 0) {
                      event.preventDefault();
                      handleOptionSubmit(selected[selected.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
          {error && <Input.Error>{error}</Input.Error>}
        </Input.Wrapper>
      </Combobox.DropdownTarget>
      <Combobox.Dropdown ref={ref} classNames={{ dropdown: classes.dropdown }}>
        <Combobox.Options>
          {data.map((item, idx) => {
            const { disabled, label, value } = getPropsFromComboboxItem(item);
            return (
              <Combobox.Option key={idx} active={isActive(value)} value={value} disabled={disabled}>
                <Group gap="sm" wrap="nowrap">
                  {variant === "checkbox" && (
                    <Checkbox checked={isActive(value)} size="xs" variant="filled" />
                  )}
                  {label}
                </Group>
              </Combobox.Option>
            );
          })}
          {data.length === 0 && <Combobox.Empty>{emptyMessage}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
